import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@els/els-react--modal';
import { ELSButton } from 'components/common';
import { pluralizeUnit } from 'helpers/ui.helper';
import { ACTIVITY_THROTTLE_MS, IDLE_EVENTS } from './SessionTimeoutWarningHelper';
var SessionTimeoutWarning = function (_a) {
    var expiresAt = _a.expiresAt, warningTime = _a.warningTime, onSessionTimeout = _a.onSessionTimeout, onExtendSession = _a.onExtendSession;
    var _b = useState(false), showWarning = _b[0], setShowWarning = _b[1];
    var idleTimerRef = useRef(null);
    var warningTimerRef = useRef(null);
    var expireTimerRef = useRef(null);
    var warningActiveRef = useRef(false);
    var activitySinceLastScheduleRef = useRef(false);
    var isAutoExtendingRef = useRef(false);
    var lastActivityTimeRef = useRef(0);
    var clearTimers = useCallback(function () {
        if (idleTimerRef.current !== null) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        if (warningTimerRef.current !== null) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (expireTimerRef.current !== null) {
            clearTimeout(expireTimerRef.current);
            expireTimerRef.current = null;
        }
    }, []);
    var handleTimeout = useCallback(function () {
        warningActiveRef.current = false;
        setShowWarning(false);
        onSessionTimeout();
    }, [onSessionTimeout]);
    var scheduleWarning = useCallback(function () {
        clearTimers();
        activitySinceLastScheduleRef.current = false;
        // No valid session token yet — do not schedule anything
        if (!expiresAt) {
            return;
        }
        var now = Date.now();
        var timeUntilExpiry = Math.max(expiresAt - now, 0);
        // Hard expiry timer - always calls onSessionTimeout at absolute expiry time
        if (timeUntilExpiry > 0) {
            expireTimerRef.current = setTimeout(function () {
                handleTimeout();
            }, timeUntilExpiry);
        }
        else {
            handleTimeout();
            return;
        }
        // Set timer for warning boundary (before expiry)
        var warningBoundaryDelay = Math.max(timeUntilExpiry - warningTime, 0);
        idleTimerRef.current = setTimeout(function () {
            // Check if user was active since last schedule
            if (activitySinceLastScheduleRef.current && onExtendSession && !isAutoExtendingRef.current) {
                // User is active - silently auto-extend without showing popup
                isAutoExtendingRef.current = true;
                onExtendSession()
                    .then(function () {
                    scheduleWarning();
                })
                    .catch(function () {
                    handleTimeout();
                })
                    .finally(function () {
                    isAutoExtendingRef.current = false;
                });
            }
            else {
                // User is inactive - show popup
                warningActiveRef.current = true;
                setShowWarning(true);
                warningTimerRef.current = setTimeout(function () {
                    handleTimeout();
                }, warningTime);
            }
        }, warningBoundaryDelay);
    }, [expiresAt, warningTime, handleTimeout, clearTimers, onExtendSession]);
    var handleActivity = useCallback(function () {
        // Only track activity if the page is actually visible
        if (document.hidden) {
            return;
        }
        // If warning popup is already shown, don't respond to activity
        if (warningActiveRef.current) {
            return;
        }
        var now = Date.now();
        if (now - lastActivityTimeRef.current < ACTIVITY_THROTTLE_MS) {
            return;
        }
        lastActivityTimeRef.current = now;
        activitySinceLastScheduleRef.current = true;
    }, []);
    var handleDismiss = useCallback(function () {
        setShowWarning(false);
    }, []);
    var handleExtendSession = useCallback(function () {
        clearTimers();
        warningActiveRef.current = false;
        setShowWarning(false);
        if (onExtendSession) {
            onExtendSession()
                .then(function () {
                scheduleWarning();
            })
                .catch(function () {
                onSessionTimeout();
            });
        }
        else {
            scheduleWarning();
        }
    }, [clearTimers, scheduleWarning, onExtendSession, onSessionTimeout]);
    useEffect(function () {
        IDLE_EVENTS.forEach(function (event) { return globalThis.addEventListener(event, handleActivity); });
        scheduleWarning();
        return function () {
            clearTimers();
            IDLE_EVENTS.forEach(function (event) { return globalThis.removeEventListener(event, handleActivity); });
        };
    }, [handleActivity, scheduleWarning, clearTimers]);
    if (!showWarning) {
        return null;
    }
    var warningMinutes = Math.round(warningTime / 60000);
    return (<Modal label="Session expiry warning" onClose={handleDismiss} shouldCloseOnEscape className="c-session-timeout-warning" focusTrapProps={{ initialFocus: '#btn-session-timeout-stay', returnFocusOnDeactivate: true }}>
      <div aria-labelledby="session-timeout-dialog-title" aria-describedby="session-timeout-dialog-desc">
        <h2 id="session-timeout-dialog-title" className="c-session-timeout-warning__title">
          Your session is about to expire
        </h2>
        <p id="session-timeout-dialog-desc" className="c-session-timeout-warning__description">
          Due to inactivity, your session will expire in {warningMinutes} {pluralizeUnit(warningMinutes, 'minute')}. Click &ldquo;Stay Logged In&rdquo; to remain logged in.
        </p>
        <div className="c-session-timeout-warning__footer">
          <ELSButton id="btn-session-timeout-stay" type="button" onClick={handleExtendSession}>
            Stay Logged In
          </ELSButton>
        </div>
      </div>
    </Modal>);
};
export default SessionTimeoutWarning;
